import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building, 
  Monitor, 
  Wifi, 
  Camera, 
  Lock, 
  ArrowRight,
  CheckCircle,
  Star,
  Globe,
  Smartphone,
  Settings,
  Users
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../../utils/cn';

const Commercial = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Slider state for hero section
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const sliderImages = [
    '/divisions/Commercial - Electrical/the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg',
    '/divisions/Commercial - Electrical/a-male-electrician-works-in-a-switchboard-with-an-2023-11-27-05-08-21-utc.jpg',
    '/divisions/Commercial - Electrical/man-an-electrical-technician-working-in-a-switchb-2023-11-27-04-54-33-utc.jpg',
    '/divisions/Commercial - Electrical/electrical-supplies-shot-2023-11-27-04-55-54-utc.jpg',
    '/divisions/Commercial - Electrical/robotic-arm-modern-industrial-technology-automate-2023-11-27-05-21-51-utc.jpg'
  ];

  useEffect(() => {
    // Ensure page starts from top on component mount
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === sliderImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [sliderImages.length]);

  const products = [
    {
      icon: Building,
      title: 'Building Automation',
      description: 'Intelligent building management and automation systems',
      features: ['HVAC Control', 'Lighting Management', 'Energy Monitoring', 'Access Control Integration'],
      image: '/divisions/Commercial - Electrical/hvac-technician-installing-large-modern-heat-pump-2023-11-27-05-04-45-utc.jpeg'
    },
    {
      icon: Camera,
      title: 'Security Systems',
      description: 'Comprehensive security and surveillance solutions',
      features: ['CCTV Systems', 'Access Control', 'Intrusion Detection', 'Video Analytics'],
      image: '/divisions/Commercial - Electrical/close-up-of-an-engineer-repairing-an-electrical-sy-2024-01-03-01-39-50-utc.jpg'
    },
    {
      icon: Wifi,
      title: 'Network Infrastructure',
      description: 'Enterprise networking and connectivity solutions',
      features: ['Structured Cabling', 'WiFi Networks', 'Network Switches', 'Fiber Optic Systems'],
      image: '/divisions/Commercial - Electrical/vertical-shot-of-cords-and-wiring-with-a-warning-s-2023-11-27-04-55-17-utc.jpg'
    },
    {
      icon: Monitor,
      title: 'Audio Visual Systems',
      description: 'Professional audio visual and presentation systems',
      features: ['Conference Systems', 'Digital Signage', 'Sound Systems', 'Video Walls'],
      image: '/divisions/Commercial - Electrical/dubai-metro-2024-12-05-07-58-55-utc.jpg'
    },
    {
      icon: Lock,
      title: 'Access Control',
      description: 'Advanced access control and time attendance systems',
      features: ['Card Readers', 'Biometric Systems', 'Time Attendance', 'Visitor Management'],
      image: '/divisions/Commercial - Electrical/shopping-cart-full-of-electrical-components-and-eq-2023-11-27-05-21-03-utc.jpg'
    },
    {
      icon: Smartphone,
      title: 'Communication Systems',
      description: 'Business communication and collaboration tools',
      features: ['IP Telephony', 'Video Conferencing', 'Unified Communications', 'Mobile Solutions'],
      image: '/divisions/Commercial - Electrical/robotics-engineer-working-on-maintenance-of-modern-2023-11-27-05-03-29-utc.jpg'
    }
  ];

  const solutions = [
    {
      title: 'Smart Offices',
      description: 'Intelligent office solutions for enhanced productivity and efficiency',
      applications: ['Corporate Offices', 'Co-working Spaces', 'Business Centers', 'Executive Suites'],
      image: '/divisions/Commercial - Electrical/factory-worker-cutting-metal-using-acetylene-torch-2024-12-04-07-58-50-utc.jpg'
    },
    {
      title: 'Retail Solutions',
      description: 'Technology solutions for modern retail environments',
      applications: ['Shopping Malls', 'Retail Stores', 'Supermarkets', 'Restaurants'],
      image: '/divisions/Commercial - Electrical/shopping-cart-full-of-electrical-components-and-eq-2023-11-27-05-34-35-utc.jpg'
    },
    {
      title: 'Educational Facilities',
      description: 'Technology infrastructure for educational institutions',
      applications: ['Schools', 'Universities', 'Training Centers', 'Libraries'],
      image: '/divisions/Commercial - Electrical/welding-2023-11-27-05-10-31-utc.jpg'
    },
    {
      title: 'Healthcare Systems',
      description: 'Specialized solutions for healthcare facilities',
      applications: ['Hospitals', 'Clinics', 'Medical Centers', 'Laboratories'],
      image: '/divisions/Commercial - Electrical/battery-pack-in-battery-room-in-power-plant-for-su-2023-11-27-04-54-40-utc.jpg'
    }
  ];

  const brands = [
    { name: 'ABB', logo: '/Suppliers logo/ABB.png' },
    { name: 'Schneider Electric', logo: '/Suppliers logo/schneider-logo.png' },
    { name: 'Siemens', logo: '/Suppliers logo/siemens.png' },
    { name: 'Eaton', logo: '/Suppliers logo/Eaton.jpg' },
    { name: 'Honeywell', logo: '/Suppliers logo/Honeywell-Logo.png' },
    { name: 'Legrand', logo: '/Suppliers logo/legrand-logo.png' },
    { name: 'Phoenix Contact', logo: '/Suppliers logo/phoenix-contact-logo.png' },
    { name: 'Fluke', logo: '/Suppliers logo/Fluke.png' },
    { name: 'Mitsubishi Electric', logo: '/Suppliers logo/mitsubishi-electric.png' },
    { name: 'Emerson', logo: '/Suppliers logo/emerson.png' },
    { name: 'Allen Bradley', logo: '/Suppliers logo/Allen-Bradley_logo.png' },
    { name: 'GE', logo: '/Suppliers logo/GE.png' },
    { name: 'Moxa', logo: '/Suppliers logo/Moxa.png' }
  ];

  const services = [
    {
      title: 'Consultation',
      description: 'Expert consultation and system design services'
    },
    {
      title: 'Integration',
      description: 'Seamless system integration and deployment'
    },
    {
      title: 'Support',
      description: 'Ongoing technical support and maintenance'
    },
    {
      title: 'Training',
      description: 'User training and knowledge transfer programs'
    }
  ];

  const features = [
    {
      title: 'Energy Efficiency',
      description: 'Smart systems that optimize energy consumption and reduce operational costs',
      icon: Settings
    },
    {
      title: 'User Experience',
      description: 'Intuitive interfaces and seamless user experiences across all systems',
      icon: Users
    },
    {
      title: 'Scalability',
      description: 'Flexible solutions that grow with your business requirements',
      icon: Building
    },
    {
      title: 'Integration',
      description: 'Unified platforms that integrate multiple building systems',
      icon: Monitor
    }
  ];

  const benefits = [
    {
      title: 'Increased Productivity',
      description: 'Modern technology solutions that enhance workplace productivity and collaboration.',
      percentage: '35%'
    },
    {
      title: 'Energy Savings',
      description: 'Smart building automation systems that significantly reduce energy consumption.',
      percentage: '25%'
    },
    {
      title: 'Security Enhancement',
      description: 'Advanced security systems providing comprehensive protection and monitoring.',
      percentage: '90%'
    },
    {
      title: 'Cost Reduction',
      description: 'Optimized operations and maintenance leading to substantial cost savings.',
      percentage: '20%'
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/divisions/Commercial - Electrical/the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg"
            alt="Commercial Electrical Systems"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80" />
          
          {/* Geometric Pattern Overlay */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid-pattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid-pattern)" />
              </svg>
            </div>
          </div>

          {/* Floating Particles */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-burnt-orange rounded-full opacity-30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [-20, 20, -20],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
          >
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t('divisions.commercial.title')}
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed text-gray-300">
                 We specialize in delivering top-tier commercial electrical sales and services 
                tailored to meet the unique needs of on-shore and off-shore oil and gas field 
                industries. From electrical products, light fixtures, industrial tools and data / 
                information technology items, we offer comprehensive solutions to ensure 
                your operations run safely, efficiently, and without interruption. 
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-burnt-orange to-metallic-gold hover:from-burnt-orange/90 hover:to-metallic-gold/90 text-white border-0">
                    Get Quote
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Link to="/companies">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                    Our Partners Companies
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 relative overflow-hidden">
                
                <div className="absolute inset-0 rounded-2xl overflow-hidden">

                  <div className="relative mb-6">
                  <img 
                    src="/divisions/Commercial - Electrical/close-up-of-an-engineer-repairing-an-electrical-sy-2024-01-03-01-39-50-utc.jpg"
                    alt="Commercial Electrical Solutions"
                    
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl" />
                </div>
                  

                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl" />
                
                <div className="relative z-10 text-center text-white">
                  <Building className="w-16 h-16 text-burnt-orange mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold mb-4">Commercial Electrical Solutions</h3>
                  <p className="text-lg text-gray-300">
                    Providing Expert Commercial 
                    Electrical Sales and Services
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Infrastructure Gallery Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal mb-6">
              Commercial Electrical Infrastructure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our comprehensive commercial electrical solutions, from advanced automation systems 
              to industrial power infrastructure and smart building technologies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                src: "/divisions/Commercial - Electrical/the-assembly-of-the-electrical-panel-electrician-2023-11-27-04-53-19-utc.jpg",
                title: "Electrical Control Panels",
                description: "Advanced electrical panel systems with circuit breakers and control systems"
              },
              {
                src: "/divisions/Commercial - Electrical/air-ventilation-system-on-the-ceiling-in-a-large-w-2023-11-27-05-09-28-utc.jpg",
                title: "HVAC Systems",
                description: "Commercial rooftop HVAC units and climate control systems"
              },
              {
                src: "/divisions/Commercial - Electrical/robotic-arm-modern-industrial-technology-automate-2023-11-27-05-21-51-utc.jpg",
                title: "Industrial Control Systems",
                description: "Sophisticated industrial electrical cabinets and automation controls"
              },
              {
                src: "/divisions/Commercial - Electrical/vertical-of-a-power-station-for-power-supply-2023-11-27-05-27-38-utc.jpg",
                title: "Power Transmission",
                description: "High-voltage power lines and electrical transmission infrastructure"
              },
              {
                src: "/divisions/Commercial - Electrical/vintage-ceramic-isolators-on-power-substation-2023-11-27-05-21-47-utc.jpg",
                title: "Electrical Substations",
                description: "High-voltage electrical substations and power distribution equipment"
              },
              {
                src: "/divisions/Commercial - Electrical/heat-pump-2023-11-27-05-32-49-utc.jpg",
                title: "Smart Buildings",
                description: "Modern commercial buildings with integrated electrical and automation systems"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-200">{item.description}</p>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-charcoal mb-3 group-hover:text-burnt-orange transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Commercial Solutions
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Comprehensive technology solutions designed for modern commercial environments.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                >
                  <Card className="h-full hover:scale-105 transition-transform duration-300 group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute top-4 right-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg group-hover:text-burnt-orange transition-colors duration-300">{product.title}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Measurable Benefits
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Our commercial solutions deliver tangible benefits and measurable improvements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-4xl font-bold text-burnt-orange mb-3">
                  {benefit.percentage}
                </div>
                <h3 className="text-lg font-semibold text-charcoal mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Key Features
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Advanced features that set our commercial solutions apart from the competition.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-charcoal to-burnt-orange rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-charcoal mb-3">{feature.title}</h3>
                  <p className="text-slate">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Industry Applications
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Tailored solutions for diverse commercial and institutional environments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full group overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-xl font-semibold mb-2">{solution.title}</h3>
                    </div>
                  </div>
                  <CardHeader>
                    <CardDescription className="text-base">{solution.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {solution.applications.map((app, appIndex) => (
                        <div key={appIndex} className="flex items-center text-sm text-slate">
                          <CheckCircle className="w-3 h-3 text-primary mr-2 flex-shrink-0" />
                          {app}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Brands Global Technology Distributes
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              We partner with leading technology companies to deliver cutting-edge solutions.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                className="flex items-center justify-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
              >
                <img 
                  src={brand.logo} 
                  alt={brand.name}
                  className="max-h-16 max-w-full object-contain"
                  onError={(e) => {
                    e.target.src = '/placeholder-logo.svg';
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Companies Section */}


      {/* Services Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/divisions/Commercial - Electrical/close-up-of-an-engineer-repairing-an-electrical-sy-2024-01-03-01-39-50-utc.jpg"
            alt="Commercial Electrical Solutions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/80 to-charcoal/90" />
          
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Professional Services
            </h2>
            <p className="text-xl max-w-3xl mx-auto text-gray-300">
              Comprehensive services to ensure successful implementation and ongoing success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="text-center group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group-hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-metallic-gold rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-burnt-orange transition-colors duration-300">{service.title}</h3>
                  <p className="text-gray-300">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center bg-gradient-to-r from-soft-gray to-white rounded-2xl p-12"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Transform Your Business
            </h2>
            <p className="text-xl text-slate mb-8 max-w-2xl mx-auto">
              Contact our commercial solutions experts to discuss how we can modernize your business environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto">
                  Schedule Consultation
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
              <Link to="/companies">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  View All Companies
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Commercial;